import { addSeconds, endOfDay, startOfDay } from "date-fns";
import { prop } from "ramda";
import {
  type AsyncValidateCallback,
  getFormAsyncErrors,
  getFormMeta,
  getFormSyncErrors,
  getFormSyncWarnings,
  type Validator,
  type WrappedFieldProps,
} from "redux-form";
import { isDateTimeISOFormat } from "widgets";

import { fileNameFieldValidation } from "@/app-utils/validation.ts";
import { KEY } from "@/settings/settings.ts";

import { selMessage } from "../messages/message.js";
import type { IAppState } from "../redux/reducer.js";
import { matchesEmail, matchesURL } from "./string.js";

const FORM_REDUX_MODULE_KEY = "form";

export interface ISelectOption<T = any> {
  value: T;
  label: string;
}

// to be used in the context of redux-form fields
export const fieldIsInvalid = (field: Partial<WrappedFieldProps>): boolean =>
  (field.meta.dirty ||
    field.meta.touched ||
    (!!field.meta.initial && !(Array.isArray(field.meta.initial) && field.meta.initial.length === 0))) &&
  field.meta.invalid;

/*
 getting directly the getFormMeta() elements (e.g. in order to find out which fields are in error state)
 is misleading since the field might have been unregistered in between, but they will still be provided by redux-form
 (is this a bug?), so we need to check ourselves what fields are actually currently registered using the
 "count" property from the 'registeredFields' object.
*/
const getRegisteredFields = (formName: string, state: IAppState) => {
  // same type as getFormMeta(..)
  const form = state[FORM_REDUX_MODULE_KEY][formName];
  const registeredFields = form?.registeredFields;
  const formMeta = getFormMeta(formName)(state);
  const registeredFieldsFormMeta = {};

  if (registeredFields) {
    for (let i = 0; i < Object.keys(registeredFields).length; i++) {
      const key: string = Object.keys(registeredFields)[i];
      const registeredFieldFormMeta = prop(key, formMeta); // deep get since we should also support field arrays
      if (registeredFields[key].count > 0 && !!registeredFieldFormMeta) {
        registeredFieldsFormMeta[key] = registeredFieldFormMeta;
      }
    }
  }
  return registeredFieldsFormMeta;
};

export const getFormError = (formName: string, state: IAppState) => {
  const formSyncErrors = getFormSyncErrors(formName)(state);
  const formAsyncErrors = getFormAsyncErrors(formName)(state);
  const formMeta = getRegisteredFields(formName, state);

  for (let i = 0; i < Object.keys(formMeta).length; i++) {
    const key: string = Object.keys(formMeta)[i];

    if (formMeta[key].touched) {
      if (formSyncErrors && prop<string>(key, formSyncErrors)) {
        return prop<string>(key, formSyncErrors);
      }
      if (formAsyncErrors && prop<string>(key, formAsyncErrors)) {
        return prop<string>(key, formAsyncErrors);
      }
    }
  }
  return undefined;
};

export const getFormWarning = (formName: string, state: IAppState) => {
  const getFormWarnings = getFormSyncWarnings(formName)(state);

  const formMeta = getRegisteredFields(formName, state);

  for (let i = 0; i < Object.keys(formMeta).length; i++) {
    const key: string = Object.keys(formMeta)[i];

    if (formMeta[key].touched) {
      if (getFormWarnings && prop<string>(key, getFormWarnings)) {
        return prop<string>(key, getFormWarnings);
      }
    }
  }
  return undefined;
};

export const fieldIsInWarnState = (field: Partial<WrappedFieldProps>): boolean => {
  if (field.meta) {
    return (field.meta.dirty || field.meta.touched || !!field.meta.initial) && !!field.meta.warning;
  }

  return false;
};

export const fileNameValidation: Validator = (value: any, _allValues?: any, _props?: any) => {
  if (value?.startsWith(".")) {
    return "Creation of hidden resources is not allowed.";
  }
  return fileNameFieldValidation(value);
};

export const specialCharacterValidation: Validator = (value: any, _allValues?: any, _props?: any) => {
  if (value?.match(/[:*?"<>|/\\]/)) {
    return selMessage({ id: "form.input.noSpecialChars" });
  }
};

export const noBlankValidation: Validator = (value: any, _allValues?: any, _props?: any) => {
  if (value?.match(/[ ]/)) {
    return selMessage({ id: "form.input.noBlanks" });
  }
};

export const simpleCharactersValidation: Validator = (value: any, _allValues?: any, _props?: any) => {
  if (!value?.match(/^[-a-zA-Z0-9_ ]+$/)) {
    return selMessage({ id: "form.input.onlySimpleChars" });
  }
};

export const simpleFieldValidation: Validator = (value: string | number, _allValues?: any, _props?: any) => {
  if (typeof value === "string" || typeof value === "undefined" || value == null) {
    return !value || value.trim().length === 0 ? selMessage({ id: "form.input.required" }) : undefined;
  }
  if (typeof value === "number") {
    // take care of not interpreting 0 value as a falsy value
    return value == undefined ? selMessage({ id: "form.input.required" }) : undefined;
  }
};

export const simpleDropdownMultiSelectFieldValidation: Validator = (value: any, _allValues?: any, _props?: any) => {
  return !value || value.length === 0 ? selMessage({ id: "form.selection.required" }) : undefined;
};

export const simpleDropdownFieldValidation: Validator = (value: any, _allValues?: any, _props?: any) => {
  return !value ? selMessage({ id: "form.selection.required" }) : undefined;
};

export const simpleDateValidation: Validator = (value: string) => {
  if (!value?.trim()) {
    // value is null or undefined
    return selMessage({ id: "form.input.required" });
  }

  // applicable in editing grids where the date editing cell will translate to a string formatted date
  if (!isDateTimeISOFormat(value)) {
    return selMessage({ id: "form.input.unparseableDate" });
  }
};

/**
 * Computes the number of days from the beginning of todays date until the end of the day
 * of the provided date.
 * @param date
 */
export const getDiffInDays = (date: string) => {
  const today = startOfDay(new Date());
  const selectedDate = endOfDay(new Date(date));
  return Math.floor((selectedDate.getTime() - today.getTime()) / 1000 / 60 / 60 / 24);
};

/**
 * Computes the number of seconds from the current time until the end of the day
 * of the provided date.
 *
 * Adds one extra second to the result because the diff operation is computed until
 * 23:59:59 h.
 * @param date
 */
export const getDiffInSeconds = (date: string) => {
  const today = new Date();
  const selectedDate = addSeconds(endOfDay(new Date(date)), 1);
  return Math.floor((selectedDate.getTime() - today.getTime()) / 1000);
};

export const lifeTimeValidation: Validator = (date: string) => {
  const end = endOfDay(new Date(date));
  if (end < new Date()) {
    return selMessage({ id: "form.input.selectedBeforeToday" });
  }

  const lifetimeInDays = getDiffInDays(date);
  return lifetimeInDays < 0 || lifetimeInDays > 365 ? selMessage({ id: "form.input.invalidNumOfDays" }) : undefined;
};

export const emailValidation: Validator = (value: any, _allValues?: any, _props?: any) => {
  return !value || value.trim().length === 0
    ? selMessage({ id: "form.input.required" })
    : !matchesEmail(value)
      ? selMessage({ id: "form.input.invalidEmail" })
      : undefined;
};

export const urlValidation = (value) => {
  return !value || value.trim().length === 0
    ? selMessage({ id: "form.input.required" })
    : !matchesURL(value)
      ? selMessage({ id: "form.input.invalidUrl" })
      : undefined;
};

export function handleTabInForm(e) {
  // Capture the TAB key and prevent propagation to parent handlers.
  //   stopPropagation() keeps the event from propagating to parent elements.
  //   onKeyDownCapture={} catches the event before it is queued to the parents.
  //   activating preventDefault() would prevent tabbing from form field to form field, too.
  if (e.key === KEY.TAB) {
    e.stopPropagation();
  }
}

export function getSelectedOption<T = ISelectOption>(
  options: T[],
  selectedIdentifier: any,
  getIdentifier: (option: T) => any = (o: T) => (o as any).value,
): T {
  return selectedIdentifier != undefined && options && options.find((o: T) => getIdentifier(o) === selectedIdentifier);
}

// redux-form function to set to prop "shouldAsyncValidate" when we want the async validate to be triggered just after custom initialization
export function shouldAsyncValidateEvenIfPristineOrInitialized(params: AsyncValidateCallback<FormData, any>) {
  if (!params.syncValidationPasses) {
    return false;
  }
  // @ts-ignore
  switch (params.trigger) {
    case "blur":
    case "change":
      // blurring or changing
      return true;
    case "submit":
      // by default the async validate is NOT trigger if form is dirty or was never initialized
      // WAS: return !pristine || !initialized
      // but in our code we often want to validate after initialization of values (e.g. copied from former form)
      return true;
    default:
      return false;
  }
}
